import React, { useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';

function formatAddress(address) {
  return `${address.street}, ${address.city}, ${address.state} ${address.zip}`;
}

// Admin-only form to rename a system and reassign its technician.
// onSave({ id, description, serviced_by }) returns a promise that rejects
// with the server's message.
function EquipmentEditForm({ item, employees, onSave, close }) {
  const [name, setName] = useState(item.description || '');
  const [servicedBy, setServicedBy] = useState(item.serviced_by ? `${item.serviced_by}` : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // The list holds active employees; keep the current technician selectable
  // even if they have since been made inactive.
  const options = [...employees];
  if (item.serviced_by && !options.some((e) => e.id === item.serviced_by)) {
    options.unshift({ id: item.serviced_by, name: item.technician || `Employee #${item.serviced_by}` });
  }

  const submit = (e) => {
    e.preventDefault();
    if (saving || !name.trim()) return;

    setSaving(true);
    setError(null);
    onSave({ id: item.id, description: name.trim(), serviced_by: servicedBy ? Number(servicedBy) : null })
      .then(close)
      .catch((err) => {
        setError(err.message);
        setSaving(false);
      });
  };

  return (
    <form className="lookup-equipment-form" onSubmit={submit}>
      <label className="lookup-equipment-field">
        <span>System name</span>
        <input
          type="text"
          className="browser-default"
          value={name}
          maxLength={255}
          autoFocus
          onChange={(e) => setName(e.target.value)}
        />
      </label>

      <label className="lookup-equipment-field">
        <span>Technician</span>
        <select className="browser-default" value={servicedBy} onChange={(e) => setServicedBy(e.target.value)}>
          <option value="">Unassigned</option>
          {options.map((employee) => (
            <option key={employee.id} value={employee.id}>{employee.name}</option>
          ))}
        </select>
      </label>

      {error && <div className="lookup-equipment-error">{error}</div>}

      <div className="lookup-equipment-form-actions">
        <button type="button" className="lookup-equipment-btn" disabled={saving} onClick={close}>
          <CloseIcon /><span>Cancel</span>
        </button>
        <button type="submit" className="lookup-equipment-btn primary" disabled={saving || !name.trim()}>
          <DoneIcon /><span>{saving ? 'Saving...' : 'Save'}</span>
        </button>
      </div>
    </form>
  );
}

// canEdit (admins), employees and onSave turn on the edit form for each system.
export default function LocationEquipmentList({ addresses = [], equipment = [], canEdit = false, employees = [], onSave }) {
  const [editingId, setEditingId] = useState(null);

  if (!addresses.length) return null;

  return (
    <div className="lookup-location-list">
      {addresses.map((address) => {
        const systems = equipment.filter((item) => item.address?.id === address.id);

        return (
          <section key={address.id} className="lookup-location-group">
            <div className="lookup-location-header">
              <h4 className="lookup-location-address">{formatAddress(address)}</h4>
              {address.description ? (
                <p className="lookup-location-desc">{address.description}</p>
              ) : null}
            </div>

            {systems.length > 0 ? (
              <ul className="lookup-location-equipment">
                {systems.map((item) => (
                  <li key={item.id} className={`lookup-location-equipment-item${editingId === item.id ? ' is-editing' : ''}`}>
                    {editingId === item.id ? (
                      <EquipmentEditForm
                        item={item}
                        employees={employees}
                        onSave={onSave}
                        close={() => setEditingId(null)}
                      />
                    ) : (
                      <div className="lookup-location-equipment-row">
                        <div className="lookup-location-equipment-text">
                          <span className="lookup-location-equipment-name">{item.description}</span>
                          <span className="lookup-location-equipment-meta">
                            {[item.type, item.frequency, item.technician].filter(Boolean).join(' · ')}
                          </span>
                        </div>
                        {canEdit && onSave ? (
                          <button
                            type="button"
                            className="lookup-equipment-edit"
                            title="Edit system"
                            aria-label={`Edit ${item.description}`}
                            onClick={() => setEditingId(item.id)}
                          >
                            <EditIcon />
                          </button>
                        ) : null}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="lookup-location-empty">No systems at this location</p>
            )}
          </section>
        );
      })}
    </div>
  );
}
